/**
 * <s-currency-toggle> — USD | INR switch in the header for visitors outside India.
 *
 * Starts `hidden`; revealed only when the visitor is detected outside India.
 * Child buttons carry `data-currency="USD"` / `data-currency="INR"` and get
 * `aria-pressed` for the active choice. Selecting one stores the preference and
 * broadcasts it so every <s-usd-price> on the page updates instantly.
 *
 * Element dataset:
 *   data-suggestions-url — Shopify browsing-context endpoint used to detect country.
 */
import {
  isOutsideIndia,
  getPreferredCurrency,
  setPreferredCurrency,
  CURRENCY_EVENT,
} from '@theme/visitor-country';

class CurrencyToggle extends HTMLElement {
  #onCurrencyChange = (event) => this.#sync(event.detail?.currency);

  #onClick = (event) => {
    const button = event.target.closest('[data-currency]');
    if (!button || !this.contains(button)) return;
    setPreferredCurrency(button.dataset.currency);
  };

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    window.addEventListener(CURRENCY_EVENT, this.#onCurrencyChange);
    this.#reveal();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    window.removeEventListener(CURRENCY_EVENT, this.#onCurrencyChange);
  }

  async #reveal() {
    if (!this.dataset.suggestionsUrl) return;
    if (!(await isOutsideIndia(this.dataset.suggestionsUrl)) || !this.isConnected) return;
    this.#sync(getPreferredCurrency());
    this.hidden = false;
  }

  #sync(currency) {
    this.querySelectorAll('[data-currency]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.currency === currency));
    });
  }
}

if (!customElements.get('s-currency-toggle')) {
  customElements.define('s-currency-toggle', CurrencyToggle);
}
